/**
 * Track15 Logo Component
 *
 * Canonical in-app Track15 lockup.
 *
 * This component should render only approved Track15 source assets from
 * `/public/brand/`. Do not introduce alternate redraws or ad hoc variants.
 */

interface Track15LogoProps {
  size?: number;
  variant?: "full" | "icon" | "wordmark";
  color?: "navy" | "coral" | "white" | "black" | "gray";
  showNexus?: boolean;
  compact?: boolean;
  className?: string;
}

const TRACK15_FULL_ASSETS = {
  navy: "/brand/track15-logo-navy.png",
  coral: "/brand/track15-logo-coral.png",
  white: "/brand/track15-logo-white.png",
} as const;

const TRACK15_ICON_ASSETS = {
  navy: "/brand/track15-icon-navy.png",
  coral: "/brand/track15-icon-coral.png",
  white: "/brand/track15-icon-white.png",
} as const;

const textColorMap = {
  navy: "#0F4C5C",
  coral: "#D55672",
  white: "#FFFFFF",
  black: "#0F4C5C",
  gray: "#0F4C5C",
} as const;

const FULL_ASPECT_RATIO = 3797 / 1104;
const ICON_ASPECT_RATIO = 1106 / 1104;

function resolveAssetColor(color: Track15LogoProps["color"]) {
  if (color === "coral" || color === "white") {
    return color;
  }

  return "navy";
}

export function Track15Logo({
  size,
  variant = "full",
  color = "navy",
  showNexus = false,
  compact = false,
  className = "",
}: Track15LogoProps) {
  const assetColor = resolveAssetColor(color);
  const defaultSize = variant === "icon" ? 48 : compact ? 80 : 120;
  const actualSize = size ?? defaultSize;
  const textColor = textColorMap[color];

  if (variant === "icon") {
    return (
      <img
        src={TRACK15_ICON_ASSETS[assetColor]}
        alt="Track15"
        width={actualSize}
        height={actualSize / ICON_ASPECT_RATIO}
        className={className}
        style={{ width: actualSize, height: "auto" }}
      />
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <img
        src={TRACK15_FULL_ASSETS[assetColor]}
        alt="Track15"
        width={actualSize}
        height={actualSize / FULL_ASPECT_RATIO}
        style={{ width: actualSize, height: "auto" }}
      />
      {showNexus && (
        <span
          className="font-medium uppercase tracking-[0.12em]"
          style={{
            fontSize: (actualSize / FULL_ASPECT_RATIO) * 0.32,
            fontFamily: '"Avenir Next", "Inter", sans-serif',
            color: textColor,
            opacity: 0.8,
          }}
        >
          Nexus
        </span>
      )}
    </div>
  );
}
